import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Create axios instance with defaults
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('apaka_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('apaka_token');
      localStorage.removeItem('apaka_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============ AUTH APIs ============
export const register = async (userData) => {
  const response = await apiClient.post('/auth/register', userData);
  return response.data;
};

export const login = async (credentials) => {
  const response = await apiClient.post('/auth/login', credentials);
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await apiClient.get('/auth/me');
  return response.data;
};

// ============ COMPANY APIs ============
export const createCompanyProfile = async (profileData) => {
  const response = await apiClient.post('/company/profile', profileData);
  return response.data;
};

export const getCompanyProfile = async () => {
  const response = await apiClient.get('/company/profile');
  return response.data;
};

export const updateCompanyProfile = async (profileData) => {
  const response = await apiClient.put('/company/profile', profileData);
  return response.data;
};

// ============ FUNDING APPLICATION APIs ============
export const createApplication = async (appData) => {
  const response = await apiClient.post('/applications', appData);
  return response.data;
};

export const getApplications = async () => {
  const response = await apiClient.get('/applications');
  return response.data;
};

export const getApplication = async (appId) => {
  const response = await apiClient.get(`/applications/${appId}`);
  return response.data;
};

export const analyzeApplication = async (appId) => {
  const response = await apiClient.post(`/applications/${appId}/analyze`);
  return response.data;
};

export const submitApplication = async (appId) => {
  const response = await apiClient.post(`/applications/${appId}/submit`);
  return response.data;
};

// ============ FUNDER APIs ============
export const createFunderProfile = async (profileData) => {
  const response = await apiClient.post('/funder/profile', profileData);
  return response.data;
};

export const getFunderProfile = async () => {
  const response = await apiClient.get('/funder/profile');
  return response.data;
};

export const getFunderLeads = async (status = null) => {
  const url = status ? `/funder/leads?status=${status}` : '/funder/leads';
  const response = await apiClient.get(url);
  return response.data;
};

export const respondToLead = async (leadId, interested, notes = null) => {
  const response = await apiClient.put(`/funder/leads/${leadId}/respond?interested=${interested}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`);
  return response.data;
};

export const analyzeCustomer = async (formData) => {
  const response = await apiClient.post('/funder/analyze-customer', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const getFunderAnalyses = async () => {
  const response = await apiClient.get('/funder/analyses');
  return response.data;
};

export const getCreditMemo = async (analysisId) => {
  const response = await apiClient.get(`/funder/analyses/${analysisId}/credit-memo`);
  return response.data;
};

// ============ ADMIN APIs ============
export const adminGetUsers = async (role = null) => {
  const url = role ? `/admin/users?role=${role}` : '/admin/users';
  const response = await apiClient.get(url);
  return response.data;
};

export const adminGetCompanies = async () => {
  const response = await apiClient.get('/admin/companies');
  return response.data;
};

export const adminGetFunders = async () => {
  const response = await apiClient.get('/admin/funders');
  return response.data;
};

export const adminGetApplications = async (status = null) => {
  const url = status ? `/admin/applications?status=${status}` : '/admin/applications';
  const response = await apiClient.get(url);
  return response.data;
};

export const adminPushToFunder = async (appId, funderId) => {
  const response = await apiClient.post(`/admin/applications/${appId}/push-to-funder?funder_id=${funderId}`);
  return response.data;
};

export const adminGetStats = async () => {
  const response = await apiClient.get('/admin/stats');
  return response.data;
};

// ============ NOTIFICATION APIs ============
export const getNotifications = async (unreadOnly = false) => {
  const response = await apiClient.get(`/notifications?unread_only=${unreadOnly}`);
  return response.data;
};

export const markNotificationRead = async (notifId) => {
  const response = await apiClient.put(`/notifications/${notifId}/read`);
  return response.data;
};

export const getUnreadCount = async () => {
  const response = await apiClient.get('/notifications/count');
  return response.data;
};

// ============ DOCUMENT APIs ============
export const uploadDocument = async (formData) => {
  const response = await apiClient.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const getDocuments = async (ownerId) => {
  const response = await apiClient.get(`/documents?owner_id=${ownerId}`);
  return response.data;
};

// ============ PRICING APIs ============
export const getPricingPlans = async () => {
  const response = await apiClient.get('/pricing/plans');
  return response.data;
};

export const upgradeSubscription = async (tier) => {
  const response = await apiClient.post(`/subscription/upgrade?tier=${tier}`);
  return response.data;
};

export const purchaseCredits = async (amount) => {
  const response = await apiClient.post(`/credits/purchase?amount=${amount}`);
  return response.data;
};

// ============ PAYMENT APIs (Stripe) ============
export const getPaymentPackages = async () => {
  const response = await apiClient.get('/payments/packages');
  return response.data;
};

export const createSubscriptionCheckout = async (tier, billingCycle = 'monthly') => {
  const response = await apiClient.post(`/payments/checkout/subscription?tier=${tier}&billing_cycle=${billingCycle}`);
  return response.data;
};

export const createCreditsCheckout = async (packageId) => {
  const response = await apiClient.post(`/payments/checkout/credits?package_id=${packageId}`);
  return response.data;
};

export const getPaymentStatus = async (sessionId) => {
  const response = await apiClient.get(`/payments/status/${sessionId}`);
  return response.data;
};

export const getPaymentHistory = async () => {
  const response = await apiClient.get('/payments/history');
  return response.data;
};

// ============ PROFILE HEALTH APIs ============
export const getCompanyProfileHealth = async () => {
  const response = await apiClient.get('/company/profile/health');
  return response.data;
};

export const getFunderProfileHealth = async () => {
  const response = await apiClient.get('/funder/profile/health');
  return response.data;
};

// ============ CREDIT MEMO EXPORT APIs ============
export const exportCreditMemoPDF = async (analysisId) => {
  const response = await apiClient.get(`/funder/analyses/${analysisId}/export/pdf`, {
    responseType: 'blob'
  });
  return response.data;
};

export const exportCreditMemoWord = async (analysisId) => {
  const response = await apiClient.get(`/funder/analyses/${analysisId}/export/word`, {
    responseType: 'blob'
  });
  return response.data;
};

// ============ REFERENCE DATA APIs ============
export const getCountries = async () => {
  const response = await apiClient.get('/reference/countries');
  return response.data.countries;
};

export const getSectors = async () => {
  const response = await apiClient.get('/reference/sectors');
  return response.data.sectors;
};

export const getFunderTypes = async () => {
  const response = await apiClient.get('/reference/funder-types');
  return response.data.funder_types;
};

// ============ HEALTH CHECK ============
export const healthCheck = async () => {
  const response = await apiClient.get('/health');
  return response.data;
};

export default apiClient;
